// Ce qu'on GARDE d'un écran, quand on clique « Garder ».
//
// POURQUOI CE MODULE EST À PART, ET TESTÉ
// ----------------------------------------
// Ces fonctions décident du contenu d'un document qui part chez un tiers —
// comptable, confrère, laboratoire, avocat. Une entrée mal formée ne casse rien
// à l'écran : elle produit une fiche qui a l'air correcte et dit une chose
// fausse, ou incomplète, à quelqu'un qui n'a pas les moyens de la vérifier.
//
// Elles vivaient d'abord dans `legacy.js`, qui touche au DOM au chargement et
// n'est donc pas importable en test. C'est exactement le critère que le repo se
// donne pour ranger un morceau dans `src/lib/` : testable sans DOM ni réseau.

import { eur2, pctv, fdate } from "./format.js";

/**
 * Une entrée de presse-papier pour « Combien je paie X ».
 *
 * `pa` = retour de `domi.prix_achat`, `mg` = retour de `domi.marge` (peut manquer).
 */
export function entreePrix(pa, mg) {
  if (!pa || !pa.cip13) return null;

  const ug = Number(pa.derniere_livraison_ug || 0);
  // Chaque morceau dit d'OÙ vient le chiffre. Un prix de revient sans sa date de
  // livraison ni son nombre de livraisons n'est pas opposable : le destinataire
  // ne peut ni le dater ni en juger la représentativité.
  const morceaux = [
    pa.prix_catalogue != null ? `catalogue ${eur2(pa.prix_catalogue)}` : null,
    pa.prix_remise_dernier != null
      ? `dernier remisé ${eur2(pa.prix_remise_dernier)}`
        + (pa.remise_pct_dernier != null ? ` (-${pctv(pa.remise_pct_dernier)})` : "")
      : null,
    pa.derniere_livraison_date
      ? `dernière livraison ${pa.derniere_livraison_date}`
        + (pa.derniere_livraison_labo ? ` chez ${pa.derniere_livraison_labo}` : "")
        + (ug > 0 ? `, ${ug} UG` : "")
      : null,
    pa.nb_livraisons_ytd ? `${pa.nb_livraisons_ytd} livraison(s) sur l'année` : null,
    mg && mg.marge_ht != null
      ? `marge ${eur2(mg.marge_ht)}` + (mg.marge_pct != null ? ` (${pctv(mg.marge_pct)})` : "")
      : null,
  ].filter(Boolean);

  return {
    // Stable d'un rendu à l'autre : re-chercher le même produit MET À JOUR
    // l'entrée au lieu d'en créer une seconde sur la fiche.
    id: `prix:${pa.cip13}`,
    type: "prix",
    // Le CIP figure dans le titre : sur une fiche remise à un laboratoire, une
    // désignation seule ne désigne pas un produit de façon opposable — les
    // libellés varient d'un fournisseur à l'autre, le code non.
    titre: `${pa.designation || "Produit"} — ${pa.cip13}`,
    // Le prix de revient PONDÉRÉ répond à la question ; les autres l'expliquent.
    // Il est UG comprises, c'est-à-dire par unité REÇUE et non facturée — c'est
    // la seule mesure qui ne surestime pas le prix d'achat réel.
    valeur: eur2(pa.prix_revient_pondere_ytd) + " HT / unité reçue",
    detail: morceaux.join(" · "),
    source: "Dans le Bureau · Combien je paie",
  };
}

/**
 * Une entrée pour une tuile de l'écran d'accueil.
 * `valeurFormatee` est passée telle qu'AFFICHÉE : la fiche doit dire le même
 * chiffre que l'écran, au caractère près, sinon elle ouvre un doute.
 */
export function entreeSignal(signal, valeurFormatee) {
  if (!signal) return null;
  return {
    id: `signal:${signal.cle ?? signal.titre}`,
    type: signal.typePressePapier ?? "note",
    titre: signal.titre,
    valeur: valeurFormatee,
    detail: signal.detail ?? null,
    source: "Dans le Bureau · écran Aujourd'hui",
  };
}

/**
 * Le taux de marge d'une période.
 *
 * ⚠️ LA COUVERTURE EST OBLIGATOIRE, et c'est la règle la plus importante de ce
 * fichier. L'écran Pilotage l'énonce déjà pour lui-même : « un taux calculé sur
 * 73 % des achats sans le dire est plus dangereux que pas de taux du tout ».
 * Sur une fiche transmise, c'est pire encore — le destinataire n'a aucun moyen
 * de soupçonner que le chiffre porte sur une partie seulement des achats. Une
 * entrée sans couverture est donc REFUSÉE, pas dégradée.
 */
export function entreeMarge(dernier, evolution, libellePeriode) {
  if (!dernier || dernier.valeur == null || dernier.couverture == null) return null;
  const morceaux = [
    `calculé sur ${pctv(dernier.couverture)} de vos achats`,
    dernier.ecartees ? `${dernier.ecartees} lignes sans prix de vente connu` : null,
    dernier.achat != null ? `achats ${eur2(dernier.achat)}` : null,
    evolution && evolution.possible
      ? `évolution ${evolution.delta > 0 ? "+" : ""}${evolution.delta} pts sur la période`
      // Refuser de conclure est une information : la fiche le dit aussi, sinon
      // le destinataire croirait à une absence de mesure.
      : evolution ? `évolution non comparable — ${evolution.raison}` : null,
  ].filter(Boolean);

  return {
    id: `marge:${dernier.periode}`,
    type: "marge",
    titre: `Taux de marge — ${libellePeriode ?? dernier.periode}`,
    valeur: `${pctv(dernier.valeur)}`,
    detail: morceaux.join(" · "),
    source: "Dans le Bureau · écran Pilotage",
  };
}

/**
 * Une tranche de l'échéancier.
 *
 * Les trois tranches sont DISJOINTES et l'entrée le redit : « De 7 à 30 jours »
 * démarre à J+7 et n'englobe pas la semaine. Sans cette précision, deux lignes
 * de la fiche sembleraient se contredire — le défaut exact qui avait été corrigé
 * à l'écran.
 */
export function entreeEcheance(cle, libelle, montant, nbFactures) {
  if (montant == null) return null;
  return {
    id: `echeance:${cle}`,
    type: "echeance",
    titre: `À payer — ${libelle}`,
    valeur: eur2(montant),
    detail: [nbFactures || null, "tranche disjointe des autres"].filter(Boolean).join(" · "),
    source: "Dans le Bureau · échéancier",
  };
}

/** Libellés des trois natures de dégradation, tels qu'ils sortent du SQL. */
const ALERTE_LIBELLE = {
  remise: "Baisse de remise",
  prix: "Hausse du prix catalogue",
  port: "Frais de port apparus",
};

/**
 * Une condition d'achat dégradée, sous une forme qu'on peut OPPOSER au fournisseur.
 *
 * ⚠️ DEUX REFUS, et ce sont eux qui font tout l'intérêt de cette fonction.
 *
 * 1. `opposable !== true` → refus. La détection compare deux observations tirées
 *    d'une timeline qui unit les snapshots catalogue (DigiPharmacie) et les lignes
 *    de factures réelles. Quand les deux bouts ne viennent pas d'une facture, la
 *    « dégradation » peut n'être qu'un changement de source. Ces lignes restent
 *    utiles À L'ÉCRAN, comme signal ; les mettre dans un document envoyé à un
 *    fournisseur, c'est risquer une réclamation fondée sur un artefact — et une
 *    seule réclamation fausse coûte plus cher en crédibilité que dix vraies n'en
 *    rapportent.
 *
 * 2. Pas de `num_facture_apres` → refus. Une réclamation qui ne désigne pas la
 *    pièce ne se vérifie pas : le destinataire ne peut ni la retrouver ni y
 *    répondre, et elle se classe sans suite.
 *
 * Le montant, lui, n'est PAS obligatoire : « la remise est passée de 20 % à 15 %
 * sur votre facture FA-4471 » est opposable même sans chiffrer le préjudice. Il
 * s'affiche quand il est calculable, et l'entrée reste recevable sinon.
 */
export function entreeAlerte(a) {
  if (!a || a.opposable !== true) return null;
  if (!a.num_facture_apres) return null;

  const ecart =
    a.unite === "€"
      ? eur2(a.valeur_apres)
      : `${pctv(a.valeur_avant)} → ${pctv(a.valeur_apres)}`;

  const morceaux = [
    a.type === "port" ? null : `${ALERTE_LIBELLE[a.type] ?? a.type} : ${ecart}`,
    // La pièce d'abord : c'est ce que le destinataire ouvrira pour vérifier.
    `facture ${a.num_facture_apres}${a.date_apres ? ` du ${fdate(a.date_apres)}` : ""}`,
    a.num_facture_avant
      ? `contre ${a.num_facture_avant}${a.date_avant ? ` du ${fdate(a.date_avant)}` : ""}`
      : null,
    a.quantite_apres != null ? `${a.quantite_apres} unité(s) livrée(s)` : null,
  ].filter(Boolean);

  // L'identifiant porte le produit ET la pièce : deux dégradations du même article
  // sur deux factures différentes sont deux réclamations distinctes, pas un doublon.
  const cle = a.produit_key || a.cip13 || a.designation || a.fournisseur || "?";

  return {
    id: `alerte:${a.type}:${cle}:${a.num_facture_apres}`,
    type: "alerte",
    // Comme pour `entreePrix` : le CIP désigne le produit de façon opposable là où
    // une désignation seule varie d'un fournisseur à l'autre.
    titre:
      a.type === "port"
        ? `Frais de port — ${a.fournisseur || "fournisseur inconnu"}`
        : `${a.designation || "Produit"}${a.cip13 ? ` — ${a.cip13}` : ""}`,
    // Ce qui se réclame passe en valeur. Sans chiffrage possible, on affiche
    // l'écart constaté plutôt qu'un montant inventé.
    valeur: a.impact_eur != null ? `${eur2(a.impact_eur)} de surcoût` : ecart,
    // Le montant CHIFFRÉ, gardé à part de `valeur` qui est une chaîne formatée.
    // Additionner « 1 234,56 € » en JavaScript donne NaN : le total par
    // destinataire a besoin du nombre, pas de son affichage.
    montantReclame: a.impact_eur != null ? Number(a.impact_eur) : 0,
    detail: morceaux.join(" · "),
    // Le destinataire de la réclamation. Sert à découper la fiche en un document
    // par fournisseur — voir `grouperAlertesParDestinataire`.
    destinataire: a.fournisseur || a.laboratoire || null,
    source: "Dans le Bureau · d'après vos factures",
  };
}

/**
 * Découpe des entrées en un groupe PAR DESTINATAIRE.
 *
 * Une réclamation a un destinataire : Alliance n'a pas à lire les conditions que
 * consent CERP. Le regroupement est donc une exigence de fond, pas une commodité
 * d'affichage.
 *
 * Les groupes sortent par montant réclamé décroissant — on écrit d'abord au
 * fournisseur chez qui il y a le plus à récupérer. Les entrées sans destinataire
 * identifié sont rassemblées à part plutôt que réparties au hasard : mieux vaut
 * une pile « à trier » visible qu'une ligne envoyée au mauvais laboratoire.
 */
export function grouperAlertesParDestinataire(entrees) {
  const parDest = new Map();

  for (const e of Array.isArray(entrees) ? entrees : []) {
    if (!e || e.type !== "alerte") continue;
    const dest = e.destinataire || "Destinataire à identifier";
    if (!parDest.has(dest)) parDest.set(dest, []);
    parDest.get(dest).push(e);
  }

  return [...parDest.entries()]
    .map(([destinataire, liste]) => ({
      destinataire,
      entrees: liste,
      // Reconstruit depuis `montantReclame`, porté par l'entrée au moment de sa
      // création : re-parser la chaîne affichée serait un piège (« 1 234,56 € »
      // n'est pas un nombre JavaScript).
      total: liste.reduce((s, e) => s + (Number(e.montantReclame) || 0), 0),
      nb: liste.length,
    }))
    .sort((a, b) => b.total - a.total || b.nb - a.nb || a.destinataire.localeCompare(b.destinataire));
}

/**
 * Un fournisseur, avec l'historique qu'on a de lui.
 *
 * `n` et `ttc` viennent de NOS factures, pas d'un catalogue : c'est ce qui rend
 * la ligne défendable si elle sert à négocier. On le dit dans la source.
 */
export function entreeFournisseur(item) {
  if (!item || !(item.slug || item.label)) return null;
  const morceaux = [
    item.n ? `${item.n} facture${item.n > 1 ? "s" : ""}` : null,
    item.last_d ? `dernier achat ${item.last_d}` : null,
    item.first_d && item.first_d !== item.last_d ? `depuis ${item.first_d}` : null,
    item.relance ? "cadence rompue — à relancer" : null,
  ].filter(Boolean);

  return {
    id: `fournisseur:${item.slug || item.label}`,
    type: "fournisseur",
    titre: item.label || item.slug,
    valeur: item.ttc != null ? `${eur2(item.ttc)} TTC achetés` : "aucun achat enregistré",
    detail: morceaux.join(" · ") || null,
    source: "Dans le Bureau · d'après vos factures",
  };
}
